import React from 'react';
import { Select } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
    GET_ALL_CHANNEL,
    SEARCH_CHANNELS_BY_NAME,
    allChannelResponse,
    SearchChannelsByNameResponse
} from '../../../graphql/queries/getChannelAll';
import { useOrderCloseChannelIds } from '../hooks/useOrderCloseChannelIds';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';

interface OrderCloseClientSelectFilterProps {
    label: string;
    value?: string[]; // channelIds as strings
    onChange: (value: string[] | undefined) => void;
    spanClassName?: string;
    placeholder?: string;
    className?: string;
}

/**
 * 关单工具专用的客户筛选组件
 * 只显示关单工具支持的客户（TEMU_SMM, TEMU_FMM, TIKTOK_CBT, TIKTOK_FBT, TIKTOK_GS, 出口易）
 */
export const OrderCloseClientSelectFilter: React.FC<OrderCloseClientSelectFilterProps> = ({
    label,
    value,
    onChange,
    spanClassName,
    placeholder = 'All',
    className
}) => {
    const { data: allData, loading: allLoading } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const [search, { data: searchData, loading: searchLoading }] = useLazyQuery<SearchChannelsByNameResponse>(SEARCH_CHANNELS_BY_NAME);
    const { allChannelIds: allowedChannelIds, loading: channelIdsLoading } = useOrderCloseChannelIds();

    const [searchText, setSearchText] = React.useState('');
    const debouncedKeyword = useDebounce(searchText, 300);

    React.useEffect(() => {
        if (debouncedKeyword && debouncedKeyword.trim().length > 0) {
            search({ variables: { keyword: debouncedKeyword.trim() } });
        }
    }, [debouncedKeyword, search]);

    // 使用统一的样式 Hook，根据语言自动返回合适的样式类名（与 InputFilterItem 保持一致）
    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    const options = React.useMemo(() => {
        // 如果渠道ID列表还没加载完成，返回空数组
        if (channelIdsLoading || allowedChannelIds.length === 0) {
            return [];
        }

        const list = (debouncedKeyword && searchData?.searchChannelsByName?.length)
            ? searchData.searchChannelsByName
            : allData?.getAllChannels || [];

        // 只显示关单工具支持的客户
        // ch.id 是字符串类型，需要转换为数字进行比较
        return list
            .filter(ch => {
                const channelId = Number(ch.id);
                return allowedChannelIds.includes(channelId);
            })
            .map(ch => ({
                label: ch.channelName,
                value: String(ch.id),
                searchText: ch.channelName
            }));
    }, [allData, searchData, debouncedKeyword, allowedChannelIds, channelIdsLoading]);

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    placeholder={placeholder}
                    value={value}
                    onChange={(val) => onChange(val && val.length > 0 ? val : undefined)}
                    loading={allLoading || searchLoading || channelIdsLoading}
                    options={options}
                    filterOption={false}
                    onSearch={(val) => setSearchText(val)}
                    className="w-full"
                    size="small"
                    maxTagCount="responsive"
                    maxTagTextLength={10}
                    tagRender={(props) => {
                        const { label, closable, onClose } = props;
                        return (
                            <span className="ant-select-selection-item inline-flex items-center bg-gray-100 text-gray-800 rounded px-2 py-1 text-sm mr-1 mb-1">
                                <span className="ant-select-selection-item-content truncate max-w-[120px]">{label}</span>
                                {closable && (
                                    <span
                                        className="ant-select-selection-item-remove ml-1 cursor-pointer hover:text-red-500"
                                        onClick={onClose}
                                    >
                                        ×
                                    </span>
                                )}
                            </span>
                        );
                    }}
                />
            </div>
        </div>
    );
};

function useDebounce<T>(value: T, delay = 300): T {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debounced;
}

export default OrderCloseClientSelectFilter;
